/**
 * EVENTSFLO-PRE-DEPLOY-TEST-DATA-CLEANUP-AND-AUDIT-V1
 *
 * SuperAdmin-only card on /admin/backups.
 * Dry-run-first by default. Apply requires the exact phrase.
 * Zero broad-delete controls — only a dry-run scan button and a
 * gated apply button.
 */
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Badge } from '../../../components/ui/badge';
import { toast } from 'sonner';
import api from '../../../lib/api';

const CONFIRM_PHRASE = 'CLEAN UP PRE DEPLOY TEST DATA';

export default function PreDeployCleanupCard() {
  const [scan, setScan] = useState(null);
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [applyResult, setApplyResult] = useState(null);

  const runDryRun = async () => {
    setBusy(true);
    setApplyResult(null);
    try {
      const r = await api.post(
        '/admin/maintenance/pre-deploy-test-data-cleanup',
        { dry_run: true },
      );
      setScan(r.data);
      toast.success('Dry-run scan complete (read-only).');
    } catch (err) {
      toast.error(
        err?.response?.data?.detail || err?.message
          || 'Dry-run failed.',
      );
    } finally {
      setBusy(false);
    }
  };

  const runApply = async () => {
    if (confirm !== CONFIRM_PHRASE) return;
    if (!window.confirm(
      'This will DELETE every TEST-tagged record across the platform. Continue?',
    )) return;
    setBusy(true);
    try {
      const r = await api.post(
        '/admin/maintenance/pre-deploy-test-data-cleanup',
        { dry_run: false, confirm },
      );
      setApplyResult(r.data);
      // re-scan to confirm
      runDryRun();
      toast.success('Cleanup applied. Audit row written.');
    } catch (err) {
      toast.error(
        err?.response?.data?.detail || err?.message
          || 'Apply failed.',
      );
    } finally {
      setBusy(false);
    }
  };

  const candidates = scan && scan.candidate_counts_by_collection;
  const candidateTotal = candidates
    ? Object.values(candidates).reduce((a, b) => a + (b || 0), 0)
    : 0;

  return (
    <Card
      id="pre-deploy-cleanup-panel"
      data-testid="pre-deploy-cleanup-card"
      className="border-zinc-800"
    >
      <CardHeader>
        <div className="flex items-start justify-between gap-3">
          <div>
            <CardTitle data-testid="pre-deploy-cleanup-title">
              Pre-deploy TEST data cleanup
            </CardTitle>
            <p
              className="text-xs text-muted-foreground mt-1 max-w-2xl"
              data-testid="pre-deploy-cleanup-readonly-note"
            >
              SuperAdmin only. Dry-run is read-only. Apply deletes
              only TEST-tagged records and requires the exact
              confirmation phrase. Real records without a TEST
              marker are never deleted.
            </p>
          </div>
          <Button
            onClick={runDryRun}
            disabled={busy}
            size="sm"
            variant="outline"
            data-testid="pre-deploy-cleanup-scan-btn"
          >
            {busy ? 'Scanning…' : 'Dry-run scan'}
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {scan && (
          <section data-testid="pre-deploy-cleanup-scan-results">
            <div className="text-xs mb-2 flex items-center gap-2">
              <Badge variant="outline">
                {scan.dry_run ? 'dry_run' : 'apply'}
              </Badge>
              <Badge variant="outline">
                read_only: {String(scan.read_only)}
              </Badge>
              <Badge variant="outline">
                production_mutation: {String(scan.production_mutation)}
              </Badge>
              <Badge variant={candidateTotal > 0 ? 'destructive' : 'outline'}>
                {candidateTotal} candidate{candidateTotal === 1 ? '' : 's'}
              </Badge>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-1 text-xs">
              {candidates && Object.entries(candidates).map(([c, n]) => (
                <div
                  key={c}
                  className="flex justify-between border-b border-zinc-800 py-0.5"
                  data-testid={`pre-deploy-cleanup-count-${c}`}
                >
                  <span className="text-muted-foreground">{c}</span>
                  <span className={n > 0 ? 'text-amber-300 font-mono' : 'font-mono'}>
                    {n}
                  </span>
                </div>
              ))}
            </div>

            {candidateTotal > 0 && (
              <div
                className="mt-4 space-y-2 border border-rose-700/40 rounded-md p-3 bg-rose-900/10"
                data-testid="pre-deploy-cleanup-apply-section"
              >
                <Label className="text-xs text-rose-200">
                  To apply, type exactly:{' '}
                  <code className="text-[10px]">{CONFIRM_PHRASE}</code>
                </Label>
                <Input
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  placeholder={CONFIRM_PHRASE}
                  data-testid="pre-deploy-cleanup-confirm-input"
                />
                <Button
                  onClick={runApply}
                  disabled={busy || confirm !== CONFIRM_PHRASE}
                  variant="destructive"
                  size="sm"
                  data-testid="pre-deploy-cleanup-apply-btn"
                >
                  Apply cleanup
                </Button>
              </div>
            )}
            {candidateTotal === 0 && (
              <div
                className="mt-2 text-xs text-emerald-300"
                data-testid="pre-deploy-cleanup-empty"
              >
                No TEST-tagged candidates found — platform is clean.
              </div>
            )}
          </section>
        )}

        {applyResult && (
          <section
            className="border border-emerald-700/40 bg-emerald-900/10 rounded-md p-3 space-y-1"
            data-testid="pre-deploy-cleanup-apply-result"
          >
            <div className="text-sm font-medium text-emerald-300">
              Cleanup applied
            </div>
            <div className="text-xs">
              <span className="text-muted-foreground">cleanup_run_id: </span>
              <code data-testid="pre-deploy-cleanup-run-id">
                {applyResult.cleanup_run_id}
              </code>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-1 text-xs">
              {applyResult.deleted_counts_by_collection
                && Object.entries(applyResult.deleted_counts_by_collection)
                  .map(([c, n]) => (
                    <div key={c} className="flex justify-between">
                      <span className="text-muted-foreground">{c}</span>
                      <span className="font-mono text-emerald-300">{n}</span>
                    </div>
                  ))}
            </div>
          </section>
        )}
      </CardContent>
    </Card>
  );
}
